import { ProjectScheduleActivity } from '../../../../api/scheduleActivities'
import { XerTask } from '../../../../utils/xer'

const COMPARE_DATE_FIELDS = [
  'target_start_date',
  'target_end_date',
  'act_start_date',
  'act_end_date',
] as const

export function normalizeScheduleDateValue(
  value: string | null | undefined,
): string {
  if (!value || !value.trim()) {
    return ''
  }
  return value.trim().slice(0, 10)
}

/** True when XER task fields differ from a previously saved activity. */
export function taskDiffersFromSaved(
  task: XerTask,
  activity: ProjectScheduleActivity,
): boolean {
  if (task.taskName !== activity.task_name) {
    return true
  }
  if (task.statusCode !== (activity.status_code || '')) {
    return true
  }
  const savedDates = activity.dates || {}
  for (const field of COMPARE_DATE_FIELDS) {
    if (
      normalizeScheduleDateValue(task.dates[field]) !==
      normalizeScheduleDateValue(savedDates[field])
    ) {
      return true
    }
  }
  return false
}

export type ScheduleUpdateSummary = {
  recalledCount: number
  changedTasks: Array<{ taskId: string; taskCode: string; taskName: string }>
  unmappedInFileCount: number
  newInFileCount: number
  softKeptOrphans: Array<{
    externalTaskId: string
    taskCode: string
    taskName: string
  }>
}

export function buildScheduleUpdateSummary(args: {
  tasks: XerTask[]
  mappedTaskIds: Set<string>
  savedActivities: ProjectScheduleActivity[]
}): ScheduleUpdateSummary {
  const { tasks, mappedTaskIds, savedActivities } = args
  const tasksById = new Map(tasks.map(task => [task.taskId, task]))
  const savedByExternalId = new Map(
    savedActivities.map(activity => [activity.external_task_id, activity]),
  )

  let recalledCount = 0
  const changedTasks: ScheduleUpdateSummary['changedTasks'] = []
  let unmappedInFileCount = 0
  let newInFileCount = 0

  tasks.forEach(task => {
    const saved = savedByExternalId.get(task.taskId)
    const isMapped = mappedTaskIds.has(task.taskId)
    if (isMapped && saved) {
      recalledCount += 1
      if (taskDiffersFromSaved(task, saved)) {
        changedTasks.push({
          taskId: task.taskId,
          taskCode: task.taskCode,
          taskName: task.taskName,
        })
      }
    }
    if (!isMapped) {
      unmappedInFileCount += 1
      if (!saved) {
        newInFileCount += 1
      }
    }
  })

  const softKeptOrphans: ScheduleUpdateSummary['softKeptOrphans'] = []
  savedActivities.forEach(activity => {
    if (!tasksById.has(activity.external_task_id)) {
      softKeptOrphans.push({
        externalTaskId: activity.external_task_id,
        taskCode: activity.task_code,
        taskName: activity.task_name,
      })
    }
  })

  return {
    recalledCount,
    changedTasks,
    unmappedInFileCount,
    newInFileCount,
    softKeptOrphans,
  }
}
